import Vue from 'vue';
import * as Sentry from '@sentry/browser';
import * as types from '../mutation-types';
import ContactAPI from '../../api/contacts';
import ConversationApi from '../../api/conversations';

export const createMessagePayload = (payload, message, isFromWhatsApp) => {
  const { content, cc_emails, bcc_emails, template_params } = message;
  payload.append('message[content]', content);
  if (isFromWhatsApp)
    payload.append('message[template_params]', JSON.stringify(template_params));

  if (cc_emails) payload.append('message[cc_emails]', cc_emails);
  if (bcc_emails) payload.append('message[bcc_emails]', bcc_emails);
};

export const createConversationPayload = ({
  params,
  contactId,
  files,
  isFromWhatsApp,
}) => {
  const {
    inboxId,
    message,
    sourceId,
    mailSubject,
    assigneeId,
    teamId,
    labels,
    force,
  } = params;
  const payload = new FormData();

  if (message) createMessagePayload(payload, message, isFromWhatsApp);

  if (files && files.length > 0) {
    files.forEach(file => payload.append('message[attachments][]', file));
  }

  payload.append('inbox_id', inboxId);
  payload.append('contact_id', contactId);
  payload.append('source_id', sourceId);
  if (isFromWhatsApp) payload.append('proactively_initiated', true);
  // Only appended when the agent confirmed the AB#7639 duplicate warning. The server casts the
  // value, so a stray "false" would not break the guard — but FormData has no way to express
  // absence other than not appending, and "not confirmed" is exactly absence.
  if (force) payload.append('force', true);
  if (!isFromWhatsApp)
    payload.append('additional_attributes[mail_subject]', mailSubject);
  payload.append('assignee_id', assigneeId);
  if (teamId) payload.append('team_id', teamId);
  if (labels?.length) {
    labels.forEach(label => payload.append('labels[]', label));
  }

  return payload;
};

const setNewConversationPayload = ({
  isFromWhatsApp,
  params,
  contactId,
  files,
}) => {
  return createConversationPayload({
    params,
    contactId,
    files,
    isFromWhatsApp,
  });
};

// Applies a merge map ({ [loserId]: survivorId }) to a conversation list, stamping
// each matched loser as resolved + merged_into_id (its OWN survivor — different
// merges of the same contact can have different survivors). Shared by the realtime
// mutation and the pending-merge replay so the rule lives in one place.
const applyMergeMap = (conversations, mergeMap) =>
  conversations.map(conversation =>
    mergeMap[conversation.id] != null
      ? {
          ...conversation,
          merged_into_id: mergeMap[conversation.id],
          status: 'resolved',
        }
      : conversation
  );

const state = {
  records: {},
  // Per-contact, PER-LOSER buffer of realtime merges so a merge that raced a
  // contactConversations fetch survives it:
  //   { [contactId]: { [loserId]: { survivorId, seq } } }
  // `seq` is the merge-event counter when THAT loser was merged. Kept per loser
  // (not one seq for the whole contact) so each buffered merge is ordered against
  // a fetch independently: a fetch dispatched at/after a loser's seq is
  // AUTHORITATIVE for that loser (its data reflects the merge — or a later revive)
  // so it is trusted as-is and that loser is pruned; a fetch dispatched BEFORE is
  // stale for that loser, so the merge is stamped in and kept. Per-loser ordering
  // is what stops a later unrelated merge from misclassifying an earlier loser's
  // fetch as stale and re-stamping a conversation the server already revived.
  pendingMerges: {},
  // Monotonic counter of merge events, used to order fetches vs events (above).
  mergeEventSeq: 0,
  // Per-contact high-water mark of the `startedAtSeq` that last WROTE records. A
  // fetch response whose startedAtSeq is below this is out-of-order (an older
  // request landing after a newer one already applied) and is discarded, so a
  // pre-merge response can't overwrite fresher data and resurrect a merged loser.
  appliedFetchSeq: {},
  // Per-contact count of in-flight fetches. Loading is derived from this (> 0) so
  // it is scoped to the contact — a fetch for contact B finishing can't clear
  // contact A's spinner, and concurrent fetches for the same contact are counted.
  fetchingByContact: {},
  // Per-contact fetch-error flag ({ [contactId]: true }). Scoped per contact so a
  // failed fetch for one contact never flips another contact's merge modal into the
  // error state, and a success/failure for one never clears or overwrites another's.
  errorByContact: {},
  uiFlags: {},
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getContactConversation: $state => id => {
    return $state.records[Number(id)] || [];
  },
  // Whether a contact's conversation list has been loaded at least once (a
  // successful fetch sets `records[id]`, even to an empty array — distinct from
  // never-fetched `undefined`). The merge modal uses this to suppress a late
  // failure's error state when a payload already loaded (overlapping fetches from
  // the sidebar/header/banner mean an old failing request can resolve after a
  // good one).
  isContactConversationLoaded: $state => id =>
    $state.records[Number(id)] !== undefined,
  // Whether a fetch is currently in flight for THIS contact (see fetchingByContact).
  isContactConversationFetching: $state => id =>
    ($state.fetchingByContact[Number(id)] || 0) > 0,
  // Whether THIS contact's most recent fetch failed (see errorByContact).
  hasContactConversationError: $state => id =>
    $state.errorByContact[Number(id)] === true,
};

export const actions = {
  create: async ({ commit }, { params, isFromWhatsApp }) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isCreating: true,
    });
    const { contactId, files } = params;
    try {
      const payload = setNewConversationPayload({
        isFromWhatsApp,
        params,
        contactId,
        files,
      });

      const { data } = await ConversationApi.create(payload);
      commit(types.default.ADD_CONTACT_CONVERSATION, {
        id: contactId,
        data,
      });

      return data;
      // No catch on purpose. This used to be `catch (error) { throw new Error(error) }`, which
      // stringifies the axios error and throws away `error.response` — callers could not read the
      // status or the body, and ConversationForm needs `error_code` to tell the AB#7639
      // duplicate-conversation conflict apart from a real failure. Letting the error propagate
      // untouched is both what the caller needs and what the linter wants (a catch that only
      // rethrows adds nothing). The finally below still runs on the error path.
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isCreating: false,
      });
    }
  },
  get: async ({ commit, state: $state }, contactId) => {
    // Numeric id: the per-contact maps are keyed/read numerically, and a string id
    // would key an entry the modal's numeric contactId never reads.
    const id = Number(contactId);
    // Snapshot the merge-event counter so SET can order this fetch vs. later merges.
    const startedAtSeq = $state.mergeEventSeq;
    commit(types.default.SET_CONTACT_CONVERSATIONS_FETCHING, { id, delta: 1 });
    commit(types.default.SET_CONTACT_CONVERSATIONS_ERROR, {
      id,
      hasError: false,
    });
    try {
      const response = await ContactAPI.getConversations(contactId);
      commit(types.default.SET_CONTACT_CONVERSATIONS, {
        id,
        data: response.data.payload,
        startedAtSeq,
      });
      commit(types.default.SET_CONTACT_CONVERSATIONS_FETCHING, {
        id,
        delta: -1,
      });
    } catch (error) {
      commit(types.default.SET_CONTACT_CONVERSATIONS_FETCHING, {
        id,
        delta: -1,
      });
      // Report the swallowed failure so a merge modal stuck on error+retry is
      // debuggable in prod (this fetch has occasional 500s / high p95).
      Sentry.captureException(error, { extra: { contactId: id } });
      // Flag it (scoped to this contact) so the UI shows error+retry instead of an
      // empty list indistinguishable from "no conversations to merge".
      commit(types.default.SET_CONTACT_CONVERSATIONS_ERROR, {
        id,
        hasError: true,
      });
    }
  },
  // F9 — cross-agent real-time sync, mirroring conversations'
  // CONVERSATION_MERGED_REALTIME (see helper/actionCable.js#onConversationMerged
  // and conversations/actions.js#conversationMergedRealtime, which dispatches
  // here). Keeps the contact's cached conversation list (the "Previous
  // Conversations" panel, F7's Merged badge) in sync without a manual refetch.
  mergeRealtime({ commit }, { contactId, sourceIds, survivorId }) {
    if (!contactId || !sourceIds?.length) return;
    commit(types.default.CONTACT_CONVERSATION_MERGED_REALTIME, {
      contactId,
      sourceIds,
      survivorId,
    });
  },
};

export const mutations = {
  [types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONTACT_CONVERSATIONS_FETCHING]: (
    $state,
    { id, delta }
  ) => {
    const next = ($state.fetchingByContact[id] || 0) + delta;
    if (next > 0) {
      Vue.set($state.fetchingByContact, id, next);
    } else {
      Vue.delete($state.fetchingByContact, id);
    }
  },
  [types.default.SET_CONTACT_CONVERSATIONS_ERROR]: (
    $state,
    { id, hasError }
  ) => {
    // Only flag an error while this contact has NOTHING loaded to show. Overlapping
    // fetches (sidebar/header/banner/modal) can resolve out of order — if a good
    // payload already landed in `records`, a later-failing fetch must not raise a
    // (false) error. Keeps the raw getter honest for any consumer, not just the
    // modal (which also guards with `!isContactConversationLoaded`).
    if (hasError) {
      if ($state.records[id] === undefined) {
        Vue.set($state.errorByContact, id, true);
      }
    } else {
      Vue.delete($state.errorByContact, id);
    }
  },
  [types.default.SET_CONTACT_CONVERSATIONS]: (
    $state,
    { id, data, startedAtSeq = -Infinity }
  ) => {
    // Default -Infinity (not Infinity): a SET with no startedAtSeq is treated as the
    // OLDEST possible fetch (stamped stale, never authoritative), and it never writes
    // the high-water mark below. A default of Infinity would be a poison-pill — a
    // seqless SET would pin appliedFetchSeq[id] to Infinity and permanently discard
    // every real (finite-seq) fetch for that contact. Today only the `get` action
    // commits SET, and it always passes a real seq; this keeps a future caller safe.
    //
    // INVARIANT (server-side, relied on by the "authoritative" branch below): the
    // authoritative rule — startedAtSeq >= loser.seq → trust server data + prune the
    // buffer — is only safe if `getConversations` observes the already-committed
    // merge (read-your-writes: read from the primary) AND the `conversation.merged`
    // broadcast is emitted post-commit. Both hold today. If a fetch ever reads from a
    // lagging replica, an "authoritative" response could prune the buffer while still
    // returning a stale `open` loser, resurrecting it with no safety net. That's an
    // infra dependency, not a client bug — flagged here so it isn't a silent
    // assumption.
    //
    // Discard an out-of-order response: an older in-flight fetch (lower startedAtSeq)
    // landing after a newer one already wrote must not overwrite the fresher data —
    // otherwise a pre-merge payload resurrects a merged loser.
    const applied = $state.appliedFetchSeq[id];
    if (applied !== undefined && startedAtSeq < applied) return;
    // Only track finite seqs as the high-water mark (never -Infinity from a seqless
    // SET), so the discard guard above stays meaningful.
    if (Number.isFinite(startedAtSeq)) {
      Vue.set($state.appliedFetchSeq, id, startedAtSeq);
    }

    const buffer = $state.pendingMerges?.[id];
    if (!buffer) {
      Vue.set($state.records, id, data);
      return;
    }
    // Partition buffered losers by whether THIS fetch started before or after each
    // loser's OWN merge event:
    //   startedAtSeq >= loser.seq → authoritative for that loser (its data already
    //     reflects the merge, or a later revive) → trust server data, prune it.
    //   startedAtSeq <  loser.seq → stale for that loser → stamp it in, keep it
    //     buffered for other in-flight stale fetches.
    // Per-loser ordering is what lets a fetch that was authoritative for an earlier
    // merge preserve that conversation's server state (e.g. a revive) even when a
    // later unrelated merge has since bumped the counter.
    const staleMap = {};
    const remaining = {};
    Object.entries(buffer).forEach(([loserId, entry]) => {
      if (startedAtSeq >= entry.seq) return;
      staleMap[loserId] = entry.survivorId;
      remaining[loserId] = entry;
    });
    Vue.set($state.records, id, applyMergeMap(data, staleMap));
    if (Object.keys(remaining).length) {
      Vue.set($state.pendingMerges, id, remaining);
    } else {
      Vue.delete($state.pendingMerges, id);
    }
  },
  [types.default.ADD_CONTACT_CONVERSATION]: ($state, { id, data }) => {
    const conversations = $state.records[id] || [];

    const updatedConversations = [...conversations];
    const index = conversations.findIndex(
      conversation => conversation.id === data.id
    );

    if (index !== -1) {
      updatedConversations[index] = { ...conversations[index], ...data };
    } else {
      updatedConversations.push(data);
    }

    Vue.set($state.records, id, updatedConversations);
  },
  [types.default.DELETE_CONTACT_CONVERSATION]: ($state, id) => {
    // Prune this contact's records AND its sibling per-contact maps so a merged +
    // never-refetched contact doesn't leak buffer/flag entries for the whole
    // session. Impact is only memory (small entries, bounded by contacts touched),
    // but there's no reason to keep them around once the records are gone.
    Vue.delete($state.records, id);
    Vue.delete($state.pendingMerges, id);
    Vue.delete($state.appliedFetchSeq, id);
    Vue.delete($state.errorByContact, id);
    Vue.delete($state.fetchingByContact, id);
  },
  [types.default.CONTACT_CONVERSATION_MERGED_REALTIME]: (
    $state,
    { contactId, sourceIds, survivorId }
  ) => {
    // Bump the event counter so fetches can be ordered against this event.
    const seq = ($state.mergeEventSeq || 0) + 1;
    $state.mergeEventSeq = seq;

    // Buffer each newly-merged loser with THIS event's seq (per loser), merging
    // with any prior buffered merges. Kept even when the list is already cached, so
    // a fetch in flight when this event arrived (and landing after) is corrected on
    // SET instead of resurfacing the loser open.
    const buffer = { ...$state.pendingMerges?.[contactId] };
    const eventMerges = {};
    sourceIds.forEach(sourceId => {
      buffer[sourceId] = { survivorId, seq };
      eventMerges[sourceId] = survivorId;
    });
    Vue.set($state.pendingMerges, contactId, buffer);

    // If the list is already cached, stamp it now for an instant UI update — but
    // ONLY this event's losers, never the whole accumulated buffer: re-stamping the
    // full map would revert a loser the server already revived and re-opened.
    const conversations = $state.records[contactId];
    if (conversations?.length) {
      Vue.set(
        $state.records,
        contactId,
        applyMergeMap(conversations, eventMerges)
      );
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
